/**
 * The client's engagement letters.
 *
 * ===========================================================================
 * THE JOIN THAT WAS MISSING
 * ===========================================================================
 * Agreements lived on their own list and on leads. Nothing on a client referred to one,
 * so a fee earner looking at a client could not see whether an engagement letter had
 * ever been sent, let alone send one — and the backend had accepted `client` as a
 * counterparty on `POST /agreements/` the whole time. The API was built, tested and
 * correct, with no interface calling it.
 *
 * ===========================================================================
 * WHY THE STATUS COLUMN SAYS MORE THAN THE STATUS
 * ===========================================================================
 * "Draft" is the word that caused the confusion this screen exists to end: a signatory
 * was added, `send` was never pressed, and everybody read "draft" as "waiting on them".
 * So a draft row says, in words, that nothing has been sent — here as well as on the
 * agreement itself, because this is the list somebody scans while wondering why the
 * client has not replied.
 */

import {
  STAFF,
  qk,
  usePaginatedList,
  type AgreementListItem,
  type ClientDetail,
} from '@legalcrm/api';
import { Badge, Button, Card, CardHeader, Table, StatusPill, type Column } from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { formatDate } from '@/lib/format';

import { DraftEngagementDialog } from './DraftEngagementDialog';
import { agreementTone } from '../agreements/AgreementListPage';
import { WithdrawAgreementDialog, canWithdraw } from '../agreements/WithdrawAgreementDialog';

/**
 * What each status means for the person reading the row, not what it is called.
 *
 * "Draft" is the word that caused this whole confusion, so it is not the word shown.
 */
export function engagementStatusInWords(row: Pick<AgreementListItem, 'status'>): string {
  switch (row.status) {
    case 'draft':
      return 'Nothing sent yet';
    case 'sent':
      return 'Waiting for a signature';
    case 'viewed':
      return 'Opened, not yet signed';
    case 'accepted':
    case 'signed':
      return 'Signed';
    case 'active':
      return 'Signed and countersigned';
    case 'cancelled':
      // Only an issued letter is listed as cancelled: a deleted draft leaves the list.
      return 'Withdrawn';
    default:
      return '';
  }
}

export function ClientAgreementsTab({ client }: { client: ClientDetail }) {
  const navigate = useNavigate();
  const [drafting, setDrafting] = useState(false);
  const [withdrawing, setWithdrawing] = useState<AgreementListItem | null>(null);

  const list = usePaginatedList<AgreementListItem>(
    qk.sub('clients', client.id, 'agreements'),
    STAFF.agreements,
    { client: client.id },
  );

  const columns: Column<AgreementListItem>[] = [
    {
      key: 'title',
      header: 'Letter',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{row.title}</p>
          <p className="truncate text-xs text-ink-muted">Version {row.version_number}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <StatusPill label={engagementStatusInWords(row)} tone={agreementTone(row.status)} />
          {row.status === 'draft' && (
            // Said here as well as on the agreement. This is the list somebody scans
            // while wondering why the client has not replied.
            <span className="text-xs text-warning">Open it and issue it for signature</span>
          )}
        </div>
      ),
    },
    {
      key: 'sent_at',
      header: 'Issued',
      hideOnMobile: true,
      render: (row) =>
        row.sent_at ? (
          <span className="tabular-nums text-sm">{formatDate(row.sent_at)}</span>
        ) : (
          <span className="text-sm text-ink-subtle">—</span>
        ),
    },
    {
      key: 'signed_at',
      header: 'Signed',
      align: 'right',
      render: (row) =>
        row.signed_at ? (
          <span className="tabular-nums text-sm">{formatDate(row.signed_at)}</span>
        ) : (
          <span className="text-sm text-ink-subtle">—</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) =>
        canWithdraw(row.status) ? (
          // The row itself opens the agreement; this button must not do that as well.
          <div onClick={(event) => event.stopPropagation()}>
            <Can module="agreement" action="delete">
              <Button variant="ghost" size="sm" onClick={() => setWithdrawing(row)}>
                {row.status === 'draft' ? 'Delete draft' : 'Withdraw'}
              </Button>
            </Can>
          </div>
        ) : null,
    },
  ];

  const signed = client.engagement_readiness.engagement_letter.satisfied;

  return (
    <Card>
      <CardHeader
        title="Engagement letters"
        description={
          signed
            ? 'A signed engagement letter is on this file.'
            : 'Nothing on this file has been signed yet. Work on a matter stays locked until one is, where the service says so.'
        }
        actions={
          <div className="flex items-center gap-2">
            {!signed && (
              <Badge tone="warning" size="sm">
                Not engaged
              </Badge>
            )}
            {/* `create` on the agreement module, not `edit` on the client: drafting a
                letter is creating an agreement, and the roles differ. */}
            <Can module="agreement" action="create">
              <Button variant="primary" size="sm" onClick={() => setDrafting(true)}>
                Draft engagement letter
              </Button>
            </Can>
          </div>
        }
      />

      <DraftEngagementDialog
        client={client}
        open={drafting}
        onClose={() => setDrafting(false)}
      />

      {withdrawing && (
        <WithdrawAgreementDialog agreement={withdrawing} onClose={() => setWithdrawing(null)} />
      )}

      <Table
        caption={`${client.display_name} — engagement letters`}
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => navigate(`/agreements/${row.id}`)}
        emptyTitle="No engagement letter yet"
        emptyDescription="Nothing has been drafted for this client. Draft one and it opens ready to issue."
        className="rounded-none border-0 shadow-none"
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </Card>
  );
}
